/**
 * Custom hook to handle post-authentication flow
 * Combines user data sync, KYC validation, and Regtank navigation
 */

import { useCallback } from "react";
import { toast } from "react-toastify";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { useUserDataSync, UserDetail } from "./useUserDataSync";
import { useRegtankValidation } from "./useRegtankValidation";

interface UsePostAuthenticationFlowParams {
  setPendingOnBoardingAs: (value: string) => void;
  setRegtankOnboardingUrl: (url: string) => void;
  handleNavigateNext: (
    userDetail: UserDetail,
    isValidOnboardingLink: boolean,
    openModal: () => void
  ) => "NO_ACTION_REQUIRED" | void;
}

/**
 * Hook to manage the complete post-authentication flow
 * @param params - Configuration object
 * @returns Function to handle user data after authentication
 */
export const usePostAuthenticationFlow = ({
  setPendingOnBoardingAs,
  setRegtankOnboardingUrl,
  handleNavigateNext,
}: UsePostAuthenticationFlowParams) => {
  const { syncUserData } = useUserDataSync();
  const { validateAndSetRegtankUrl } = useRegtankValidation();

  const handlePostAuthentication = useCallback(
    (userDetail: UserDetail, openRegtankModal: () => void) => {
      // Set pending onboarding type for modal content
      setPendingOnBoardingAs(userDetail?.pendingOnBoardingAs || "");

      // Validate and set Regtank URL if applicable
      const { isValid: isValidOnboardingLink } = validateAndSetRegtankUrl(
        userDetail,
        setRegtankOnboardingUrl
      );

      // Check for KYC rejections
      if (userDetail?.kycStatus === KYCStatus.REJECTED) {
        toast.error("Onboarding application has been rejected.");
        return;
      }

      if (userDetail?.spvDetails?.kycStatus === KYCStatus.REJECTED) {
        toast.error("SPV Onboarding application has been rejected.");
        return;
      }

      // Sync all user data to Redux store
      syncUserData(userDetail);

      // Handle Regtank navigation
      const result = handleNavigateNext(
        userDetail,
        isValidOnboardingLink,
        openRegtankModal
      );
      if (result === "NO_ACTION_REQUIRED") {
        toast.warn("No action required at this time");
      }
    },
    [
      setPendingOnBoardingAs,
      validateAndSetRegtankUrl,
      setRegtankOnboardingUrl,
      syncUserData,
      handleNavigateNext,
    ]
  );

  return { handlePostAuthentication };
};
